import React, { useState, useEffect } from 'react';
import { Typography, Button, Box, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField, Snackbar, Alert } from '@mui/material';
import { DataGrid, GridRenderCellParams } from '@mui/x-data-grid';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import AdminLayout from '../components/AdminLayout';
import DeleteIcon from '@mui/icons-material/Delete';
import RestoreIcon from '@mui/icons-material/Restore';

const UserList = () => {
  const { api, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [deletionReason, setDeletionReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [showDeleted, setShowDeleted] = useState(false);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const endpoint = showDeleted ? '/auth/deleted-users' : '/users';
        const response = await api.get(endpoint);
        setUsers(showDeleted ? response.data.users : response.data);
      } catch (error) {
        console.error('Failed to fetch users', error);
        setSnackbar({ open: true, message: 'שגיאה בטעינת המשתמשים', severity: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [api, showDeleted]);

  const getRoleInHebrew = (role) => {
    switch (role) {
      case 'manager':
        return 'מנהל';
      case 'bus_coordinator':
        return 'סדרן אוטובוסים';
      case 'tax':
        return 'מס הכנסה';
      default:
        return role;
    }
  };

  const getStatusInHebrew = (status) => {
    switch (status) {
      case 'active':
        return 'פעיל';
      case 'inactive':
        return 'לא פעיל';
      case 'pending':
        return 'ממתין לאישור';
      default:
        return status;
    }
  };

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setDeletionReason('');
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;

    try {
      setDeleting(true);
      await api.delete(`/auth/soft-delete-user/${userToDelete.id}`, {
        data: { reason: deletionReason || 'מחיקה על ידי מנהל' }
      });
      
      setSnackbar({ 
        open: true, 
        message: `המשתמש ${userToDelete.firstName} ${userToDelete.lastName} נמחק בהצלחה`, 
        severity: 'success' 
      });
      
      // רענון הרשימה
      const endpoint = showDeleted ? '/auth/deleted-users' : '/users';
      const response = await api.get(endpoint);
      setUsers(showDeleted ? response.data.users : response.data);
      
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      setDeletionReason('');
    } catch (error) {
      console.error('Failed to delete user:', error);
      setSnackbar({ 
        open: true, 
        message: 'שגיאה במחיקת המשתמש: ' + (error.response?.data?.message || error.message), 
        severity: 'error' 
      });
    } finally {
      setDeleting(false);
    }
  };

  const handleRestoreUser = async (user) => {
    try {
      await api.post(`/auth/restore-user/${user.id}`);
      
      setSnackbar({ 
        open: true, 
        message: `המשתמש ${user.firstName} ${user.lastName} שוחזר בהצלחה`, 
        severity: 'success' 
      });
      
      // רענון הרשימה
      const endpoint = showDeleted ? '/auth/deleted-users' : '/users';
      const response = await api.get(endpoint);
      setUsers(showDeleted ? response.data.users : response.data);
      
    } catch (error) {
      console.error('Failed to restore user:', error);
      setSnackbar({ 
        open: true, 
        message: 'שגיאה בשחזור המשתמש: ' + (error.response?.data?.message || error.message), 
        severity: 'error' 
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setUserToDelete(null);
    setDeletionReason('');
  };

  const columns = [
    { field: 'id', headerName: 'ID', flex: 0.5 },
    { field: 'lastName', headerName: 'שם משפחה', flex: 1 },
    { field: 'firstName', headerName: 'שם פרטי', flex: 1 },
    { field: 'email', headerName: 'אימייל', flex: 1.5 },
    { field: 'phone', headerName: 'טלפון', flex: 1 },
    { 
      field: 'role', 
      headerName: 'תפקיד', 
      flex: 1,
      renderCell: (params) => {
        return getRoleInHebrew(params.value);
      }
    },
    { 
      field: 'status', 
      headerName: 'סטטוס', 
      flex: 1,
      renderCell: (params) => {
        return getStatusInHebrew(params.value);
      }
    },
    ...(showDeleted ? [{
      field: 'deletedAt',
      headerName: 'נמחק בתאריך',
      flex: 1,
      renderCell: (params) => {
        return params.value ? new Date(params.value).toLocaleDateString('he-IL') : '-';
      }
    }, {
      field: 'deletionReason',
      headerName: 'סיבת מחיקה',
      flex: 1.5
    }] : []),
    {
      field: 'actions',
      headerName: 'פעולות',
      flex: 1.2,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!showDeleted && (
            <>
              <Button
                component={Link}
                to={`/admin/users/edit/${params.row.id}`}
                variant="contained"
                color="primary"
                size="small"
              >
                ערוך
              </Button>
              {currentUser?.role === 'manager' && currentUser?.id !== params.row.id && (
                <IconButton
                  onClick={() => handleDeleteClick(params.row)}
                  color="error"
                  size="small"
                  disabled={deleting}
                  sx={{ 
                    '&:hover': {
                      backgroundColor: 'rgba(211, 47, 47, 0.04)',
                    }
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              )}
            </>
          )}
          {showDeleted && currentUser?.role === 'manager' && (
            <IconButton
              onClick={() => handleRestoreUser(params.row)}
              color="success"
              size="small"
              sx={{ 
                '&:hover': {
                  backgroundColor: 'rgba(76, 175, 80, 0.04)',
                }
              }}
            >
              <RestoreIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
      ),
    },
  ];

  return (
    <AdminLayout>
      <Box sx={{ mt: '64px' }}>
        <Typography variant="h4" gutterBottom align="center" mt={0}>
          ניהול משתמשים
        </Typography>

        {/* כפתורי מעבר */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant={!showDeleted ? "contained" : "outlined"}
            onClick={() => setShowDeleted(false)}
            color="primary"
          >
            משתמשים פעילים
          </Button>
          {currentUser?.role === 'manager' && (
            <Button
              variant={showDeleted ? "contained" : "outlined"}
              onClick={() => setShowDeleted(true)}
              color="secondary"
            >
              משתמשים מחוקים
            </Button>
          )}
        </Box>

        {/* עטיפת הטבלה ב-Box כדי לאפשר גלילה אופקית */}
        <Box sx={{ width: '100%', overflowX: 'auto' }}>
          <Box sx={{ minWidth: 1000 }}> {/* הגדרת מינימום רוחב */}
            <DataGrid
              rows={users}
              columns={columns}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 20]}
              disableSelectionOnClick
              disableColumnMenu
              loading={loading}
              autoHeight
              sx={{
                '& .MuiDataGrid-cell:focus': {
                  outline: 'none',
                },
                '& .MuiDataGrid-cell:focus-within': {
                  outline: 'none',
                },
                '& .MuiDataGrid-virtualScroller': {
                  overflow: 'hidden',
                },
              }}
            />
          </Box>
        </Box>
      </Box>

      {/* דיאלוג מחיקה */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>מחיקת משתמש</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            האם אתה בטוח שברצונך למחוק את המשתמש{' '}
            <strong>
              {userToDelete?.firstName} {userToDelete?.lastName}
            </strong>?
            <br />
            זו מחיקה רכה - המשתמש יוסתר אבל הנתונים יישמרו ואפשר יהיה לשחזר אותו.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="סיבת המחיקה (אופציונלי)"
            fullWidth
            variant="outlined"
            value={deletionReason}
            onChange={(e) => setDeletionReason(e.target.value)}
            multiline
            rows={2}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>ביטול</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            color="error" 
            disabled={deleting}
          >
            {deleting ? 'מוחק...' : 'מחק'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* הודעות */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminLayout>
  );
};

export default UserList;
